import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip } from 'recharts';
import { BarChart3 } from 'lucide-react';
import { DashboardCard } from './DashboardCard';
import { TOOLTIP_STYLE, TOOLTIP_ITEM_STYLE, AXIS_TICK, GRID_STROKE } from './chartTheme';

interface BarChartCardProps {
  title: string;
  data: { name: string; total: number }[];
  icon?: React.ComponentType<{ className?: string }>;
  height?: number;
  className?: string;
  tooltipFormatter?: (value: number) => string;
  yAxisFormatter?: (value: number) => string;
  onBarClick?: (name: string, total: number) => void;
}

export function BarChartCard({
  title,
  data,
  icon = BarChart3,
  height = 240,
  className,
  tooltipFormatter,
  yAxisFormatter,
  onBarClick,
}: BarChartCardProps) {
  if (data.length === 0) return null;

  const handleClick = (entry: {
    activePayload?: { payload: { name: string; total: number } }[];
  }) => {
    if (onBarClick && entry?.activePayload?.[0]) {
      const payload = entry.activePayload[0].payload;
      onBarClick(payload.name, payload.total);
    }
  };

  return (
    <DashboardCard title={title} icon={icon} className={className}>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          data={data}
          margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
          onClick={handleClick}
          style={onBarClick ? { cursor: 'pointer' } : undefined}
        >
          <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} strokeOpacity={0.5} />
          <XAxis dataKey="name" tick={AXIS_TICK} axisLine={false} tickLine={false} />
          <YAxis
            allowDecimals={false}
            tick={AXIS_TICK}
            axisLine={false}
            tickLine={false}
            tickFormatter={yAxisFormatter}
          />
          <Tooltip
            contentStyle={TOOLTIP_STYLE}
            itemStyle={TOOLTIP_ITEM_STYLE}
            formatter={
              tooltipFormatter ? (v: number | string) => tooltipFormatter(v as number) : undefined
            }
          />
          <Bar dataKey="total" radius={[8, 8, 0, 0]} minPointSize={4} fill="hsl(var(--chart-1))" />
        </BarChart>
      </ResponsiveContainer>
    </DashboardCard>
  );
}
